import { getApiClient } from '@src/shared/types/api-client'
import { BulkAssignTicketRequestDto, BulkCloseRequestDto, BulkIgnoreRequestDto, NewTicketRequestDto } from '@src/tools/error-tracker/types/BulkActions'
import { EExceptionStatus } from '@src/tools/error-tracker/types/Constants'
import { ExceptionLog } from '@src/tools/error-tracker/types/ExceptionLog'
import { ExceptionsFiltersDto } from '@src/tools/error-tracker/types/ExceptionsFiltersDto'
import { SelectValueListItem } from '@src/shared/types/SelectValueListItem'
import { MutationFunction, QueryFunctionContext, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

const SCOPE = 'exceptions'
const LIST = 'list'
const DETAIL = 'details'
const APP_NAMES_SCOPE = 'applicationNames'
const BUG_FIELD_SCOPE = 'bugField'

const keysFactory = {
  all: () => [{ scope: SCOPE }] as const,
  list: (filters: ExceptionsFiltersDto) => [{ scope: SCOPE, entity: LIST, ...filters }] as const,
  allLists: () => [{ scope: SCOPE, entity: LIST }] as const,
  detail: (id: string) => [{ scope: SCOPE, entity: DETAIL, id }] as const,
  applicationNames: () => [{ scope: APP_NAMES_SCOPE, entity: LIST }] as const,
  bugFieldAllowedValues: (fieldName: string) => [{ scope: BUG_FIELD_SCOPE, entity: LIST, fieldName }] as const,
}

export interface UpdateDevopsTicketDto {
  id: string
  devopsTicketNumber: string
  status: EExceptionStatus
}

export interface UpdateExceptionLogDto {
  devopsTicketNumber?: string
  devopsTicketNumbers?: string[]
  status?: string
  note?: string
}

export interface AddTicketToHistoryDto {
  devopsTicketNumber: string
  status: EExceptionStatus
}

function buildApplicationNames(applicationNames: string[]): SelectValueListItem[] {
  const valueList: SelectValueListItem[] = []
  if (applicationNames) {
    applicationNames.forEach((e) => valueList.push({ label: e, value: e }))
    valueList.sort((one, two) => (one.value < two.value ? -1 : 1)) // sort asc
  }
  return valueList
}

const getExceptionsList = async ({
  queryKey: [filters],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['list']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.post('ExceptionLogs/Search', filters)
  return response.data as ExceptionLog[]
}

export function useExceptionsList(filter: ExceptionsFiltersDto): [ExceptionLog[], boolean] {
  const { isFetching, data } = useQuery({
    queryKey: [...keysFactory.list(filter)],
    queryFn: getExceptionsList,
    placeholderData: [],
  })

  return [data ?? [], isFetching]
}

const updateDevopsTicket: MutationFunction<ExceptionLog, { id: string; dto: UpdateDevopsTicketDto }> = async ({
  id,
  dto,
}) => {
  const apiClient = getApiClient()
  const response = await apiClient.put(`ExceptionLogs/${encodeURIComponent(id)}/DevopsTicket`, dto)
  return response.data as ExceptionLog
}

const addTicketToHistory: MutationFunction<ExceptionLog, { id: string; dto: AddTicketToHistoryDto }> = async ({
  id,
  dto,
}) => {
  const apiClient = getApiClient()
  const response = await apiClient.post(`ExceptionLogs/${encodeURIComponent(id)}/Tickets`, dto)
  return response.data as ExceptionLog
}

const updateExceptionLog: MutationFunction<ExceptionLog, { id: string; dto: UpdateExceptionLogDto }> = async ({
  id,
  dto,
}) => {
  const apiClient = getApiClient()
  const response = await apiClient.put(`ExceptionLogs/${encodeURIComponent(id)}`, dto)
  return response.data as ExceptionLog
}

const bulkIgnoreExceptions: MutationFunction<ExceptionLog[], BulkIgnoreRequestDto> = async (dto) => {
  const apiClient = getApiClient()
  const response = await apiClient.post('ExceptionLogs/actions/ignore', dto)
  return response.data as ExceptionLog[]
}

const bulkCloseExceptions: MutationFunction<ExceptionLog[], BulkCloseRequestDto> = async (dto) => {
  const apiClient = getApiClient()
  const response = await apiClient.post('ExceptionLogs/actions/close', dto)
  return response.data as ExceptionLog[]
}

const bulkAssignTicket: MutationFunction<ExceptionLog[], BulkAssignTicketRequestDto> = async (dto) => {
  const apiClient = getApiClient()
  const response = await apiClient.post('ExceptionLogs/actions/assign-ticket', dto)
  return response.data as ExceptionLog[]
}

const createNewTicket: MutationFunction<ExceptionLog[], NewTicketRequestDto> = async (dto) => {
  const apiClient = getApiClient()
  const response = await apiClient.post('ExceptionLogs/actions/new-ticket', dto)
  return response.data as ExceptionLog[]
}

export function useUpdateDevopsTicket(): [
  MutationFunction<ExceptionLog, { id: string; dto: UpdateDevopsTicketDto }>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: updateDevopsTicket,
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: keysFactory.allLists() })
      return data
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

export function useAddTicketToHistory(): [
  MutationFunction<ExceptionLog, { id: string; dto: AddTicketToHistoryDto }>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: addTicketToHistory,
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: keysFactory.allLists() })
      return data
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

export function useUpdateExceptionLog(): [
  MutationFunction<ExceptionLog, { id: string; dto: UpdateExceptionLogDto }>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: updateExceptionLog,
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: keysFactory.allLists() })
      return data
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

export function useBulkIgnoreExceptions(): [
  MutationFunction<ExceptionLog[], BulkIgnoreRequestDto>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: bulkIgnoreExceptions,
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: keysFactory.allLists() })
      return data
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

export function useBulkCloseExceptions(): [
  MutationFunction<ExceptionLog[], BulkCloseRequestDto>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: bulkCloseExceptions,
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: keysFactory.allLists() })
      return data
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

export function useBulkAssignTicket(): [
  MutationFunction<ExceptionLog[], BulkAssignTicketRequestDto>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: bulkAssignTicket,
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: keysFactory.allLists() })
      return data
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

export function useCreateNewTicket(): [
  MutationFunction<ExceptionLog[], NewTicketRequestDto>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: createNewTicket,
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: keysFactory.allLists() })
      return data
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

const getApplicationNames = async () => {
  const apiClient = getApiClient()
  const response = await apiClient.get('ExceptionLogs/ApplicationNames')
  return response.data as string[]
}

export function useApplicationNames(): [SelectValueListItem[], boolean] {
  const { isFetching, data } = useQuery({
    queryKey: keysFactory.applicationNames(),
    queryFn: getApplicationNames,
    placeholderData: [],
  })

  const list = useMemo(() => {
    return buildApplicationNames(data ?? [])
  }, [data])

  return [list, isFetching]
}

const getBugFieldAllowedValues = async (fieldName: string) => {
  const apiClient = getApiClient()
  const response = await apiClient.get(`DevOps/BugFields/${encodeURIComponent(fieldName)}/AllowedValues`)
  return response.data as string[]
}

function buildSelectValues(values: string[]): SelectValueListItem[] {
  return values.map((v) => ({ label: v, value: v }))
}

export function useBugSeverityValues(): [SelectValueListItem[], boolean] {
  const fieldName = 'Microsoft.VSTS.Common.Severity'
  const { isFetching, data } = useQuery({
    queryKey: keysFactory.bugFieldAllowedValues(fieldName),
    queryFn: () => getBugFieldAllowedValues(fieldName),
    placeholderData: [],
  })

  const list = useMemo(() => buildSelectValues(data ?? []), [data])

  return [list, isFetching]
}

const exportDuplicateExceptions = async () => {
  const apiClient = getApiClient()
  const response = await apiClient.get('ExceptionLogs/DownloadDuplicates', { responseType: 'blob' })
  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', 'duplicate-messages.csv')
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export function useExportDuplicateExceptions(): [() => void, boolean] {
  const { mutate, isPending } = useMutation({
    mutationFn: exportDuplicateExceptions,
  })

  return [mutate, isPending]
}

export function useBugPlateformeValues(): [SelectValueListItem[], boolean] {
  const fieldName = 'Custom.Plateforme'
  const { isFetching, data } = useQuery({
    queryKey: keysFactory.bugFieldAllowedValues(fieldName),
    queryFn: () => getBugFieldAllowedValues(fieldName),
    placeholderData: [],
  })

  const list = useMemo(() => buildSelectValues(data ?? []), [data])

  return [list, isFetching]
}
